import { Text as ChakraText } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { getFieldsMapping, getFilterText } from '../../../utils/mediaLibrary'
import type { CustomMetadataResponse } from '../../interfaces'
import type {
  FilterValidationObject,
  FormValidationObject,
  SearchQueryJson,
  SearchFilterValue,
} from '../../interfaces/MediaLibrary'
import { useActiveUsers } from '../../hooks/useActiveUsers'

export const TextWrapper = ({
  text,
  textColor,
}: {
  text: string
  textColor: string
}): React.ReactElement => (
  <ChakraText lineHeight="1.5" color={textColor} fontWeight="normal" isTruncated fontSize={"14px"}>
    {text}
  </ChakraText>
)

const CreatedByText = ({
  expression,
  fieldLabel,
  textColor,
}: {
  expression: { fieldName: string; operator: string; value: SearchFilterValue }
  fieldLabel: string
  textColor: string
}): React.ReactElement => {
  const { emailToNameMap } = useActiveUsers()

  const filterText = useMemo(() => {
    const { operator, value } = expression
    let operatorText = ''
    if (operator === 'IS') {
      operatorText = 'is'
    } else if (operator === 'IS_NOT') {
      operatorText = 'is not'
    } else if (operator === 'IS_ONE_OF') {
      operatorText = 'is one of'
    } else if (operator === 'IS_NONE_OF') {
      operatorText = 'is none of'
    }

    const formatVal = (v: unknown): string => {
      const email = typeof v === 'string' ? v : (v as { value: string })?.value || ''
      if (email === '__API_USER__') return 'API user'
      const name = emailToNameMap.get(email)
      return name ? `${name} (${email})` : email
    }

    if (['IS', 'IS_NOT'].includes(operator)) {
      return ` ${operatorText} ${formatVal(value)}`
    } else if (['IS_ONE_OF', 'IS_NONE_OF'].includes(operator)) {
      const items = Array.isArray(value) ? value : []
      return ` ${operatorText} ${items.map(formatVal).join(', ')}`
    }

    return ''
  }, [expression, emailToNameMap])

  if (!filterText) {
    return (
      <TextWrapper
        text={`${fieldLabel} has an incomplete value`}
        textColor={textColor}
      />
    )
  }

  return <TextWrapper text={`${fieldLabel}${filterText}`} textColor={textColor} />
}

interface Props {
  searchQueryJson: SearchQueryJson
  validationObject: FormValidationObject
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  textColor: string
  groupId: number
  filterId: number
}

const FilterText = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    customMetadataFieldsMapping,
    textColor,
    groupId,
    filterId,
  } = props

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId].expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const fieldLabel = useMemo(
    () =>
      getFieldsMapping({
        fieldName: expression.fieldName,
        customMetadataField: customMetadataFieldsMapping[expression.fieldName],
      }).displayText,
    [searchQueryJson, groupId, filterId, customMetadataFieldsMapping],
  )

  const filterValidationObject: FilterValidationObject = useMemo(
    () => ({
      isValid: !validationObject.errors[groupId][filterId],
      error: validationObject.errors[groupId][filterId],
    }),
    [validationObject, groupId, filterId],
  )

  const filterText = useMemo(
    () => getFilterText({ expression }),
    [searchQueryJson, groupId, filterId],
  )

  if (
    !['IS_EMPTY', 'IS_NOT_EMPTY'].includes(expression.operator) &&
    (typeof expression.value === 'undefined' ||
      expression.value === null ||
      (typeof expression.value === 'string' &&
        expression.value.trim() === '') ||
      (Array.isArray(expression.value) && expression.value.length === 0) ||
      (expression.fieldName === 'createdBy' &&
        !Array.isArray(expression.value) &&
        typeof expression.value === 'object' &&
        !(expression.value as { value: string }).value))
  ) {
    return (
      <TextWrapper text={`${fieldLabel} has no value`} textColor={textColor} />
    )
  }

  if (!filterValidationObject.isValid) {
    return (
      <TextWrapper
        text={`${fieldLabel} has an invalid value`}
        textColor={textColor}
      />
    )
  }

  if (!filterText) {
    return (
      <TextWrapper
        text={`${fieldLabel} has an incomplete value`}
        textColor={textColor}
      />
    )
  }

  if (expression.fieldName === 'createdBy') {
    return (
      <CreatedByText
        expression={expression}
        fieldLabel={fieldLabel}
        textColor={textColor}
      />
    )
  }

  return ['private', 'transparency', 'published'].includes(expression.fieldName) ? (
    <TextWrapper text={`${filterText}`} textColor={textColor} />
  ) : (
    <TextWrapper text={`${fieldLabel}${filterText}`} textColor={textColor} />
  )
}

export default FilterText
